import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { CLIENT_STAGES, formatDate, labelOf } from "./gulaConstants";
import { fullName, type GulaClient } from "./gulaTypes";
import {
  SOURCE_LABELS,
  listTrash,
  restoreLead,
  trashLead,
  type TrashedLead,
} from "./leadTrash";

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

interface TrashConfirmDialogProps {
  client: GulaClient | null;
  onClose: () => void;
  onDone: () => void;
}

/** Asks before a lead that is not a real lead leaves the board. */
export const TrashConfirmDialog = ({
  client,
  onClose,
  onDone,
}: TrashConfirmDialogProps) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  if (!client) return null;

  const run = async () => {
    setBusy(true);
    try {
      await trashLead(client);
      notify(`${fullName(client)} está en la basura`, { type: "success" });
      onDone();
    } catch (error: unknown) {
      notify(errorText(error, "No se ha podido tirar"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>¿Mandar a {fullName(client)} a la basura?</DialogTitle>
          <DialogDescription>
            Sale del tablero, de las listas y de las estadísticas de leads. Su
            conversación sigue en Mensajes y, si vuelve a escribir, no entra
            otra vez como lead. Lo puedes recuperar desde «Basura».
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="destructive" disabled={busy} onClick={run}>
            A la basura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TrashListDialogProps {
  open: boolean;
  onClose: () => void;
  onRestored: () => void;
  nameOf: (id?: number | null) => string;
}

/** Admins: the leads in the trash, each one can go back to the board. */
export const TrashListDialog = ({
  open,
  onClose,
  onRestored,
  nameOf,
}: TrashListDialogProps) => {
  const notify = useNotify();
  const [items, setItems] = useState<TrashedLead[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    listTrash()
      .then(setItems)
      .catch((error: unknown) => {
        setItems([]);
        notify(errorText(error, "No se ha podido cargar la basura"), {
          type: "error",
        });
      });
  }, [open, notify]);

  const restore = async (item: TrashedLead) => {
    setBusyId(item.id);
    try {
      await restoreLead(item.id);
      setItems((current) =>
        (current ?? []).filter((entry) => entry.id !== item.id),
      );
      notify(`${item.name ?? "Lead"} vuelve al tablero`, { type: "success" });
      onRestored();
    } catch (error: unknown) {
      notify(errorText(error, "No se ha podido recuperar"), { type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Basura</DialogTitle>
          <DialogDescription>
            Leads que no lo eran (compañeros, instaladores, proveedores…).
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
          {items == null ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              La basura está vacía.
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {item.name ?? "Sin nombre"}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[
                      item.phone,
                      SOURCE_LABELS[item.source ?? ""] ?? item.source,
                      labelOf(CLIENT_STAGES, item.stage),
                      `${formatDate(item.trashed_at)}${
                        item.trashed_by ? ` · ${nameOf(item.trashed_by)}` : ""
                      }`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === item.id}
                  onClick={() => restore(item)}
                >
                  Recuperar
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
